import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Loader2, CalendarClock, Check, X, Clock, MessageSquare, FolderOpen, Sparkles,
  ShieldCheck, Stethoscope, Scale, AlertTriangle, RefreshCw,
} from "lucide-react";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const PRIORITY_TONE = { Urgent: "bg-red-50 text-red-600 border-red-200", High: "bg-amber-50 text-amber-600 border-amber-200", Standard: "bg-slate-50 text-slate-500 border-slate-200" };

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export default function ClientRequests({ approved, demoEnabled }) {
  const [rows, setRows] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [modal, setModal] = useState(null); // {type: 'propose'|'message'|'conflict', row, ...}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/telehealth/requests").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (approved) load(); }, [approved, load]);

  const regenerate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/telehealth/demo/regenerate"); toast.success(`${data.created} demo client request(s) generated`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate"); }
    finally { setBusy(false); }
  };

  const accept = async (row, startUtc = null) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/telehealth/requests/${row.request_id}/accept-appointment`, startUtc ? { start_utc: startUtc } : {});
      toast.success(`Appointment confirmed — client, attorney and RN notified. (${fmt(data.appointment?.start_utc)})`);
      setModal(null); setDrawer(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.conflicts) {
        setModal({ type: "conflict", row, conflicts: d.conflicts, alternatives: d.alternatives || [] });
      } else toast.error(formatApiErrorDetail(d) || "Could not confirm appointment");
    } finally { setBusy(false); }
  };

  if (!approved) return <div className="py-14 text-center text-sm text-slate-400" data-testid="th-empty">Client requests unlock after your application is approved.</div>;
  if (rows === null) return <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;

  if (!rows.length) {
    return (
      <div className="py-14 text-center" data-testid="th-requests-empty">
        <p className="text-sm text-slate-400">No new client requests right now.</p>
        {demoEnabled ? (
          <button onClick={regenerate} disabled={busy} data-testid="th-generate-demo-requests"
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white text-sm font-semibold disabled:opacity-50">
            <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate Demo Client Requests"}
          </button>
        ) : <p className="text-xs text-slate-300 mt-2">You'll be notified when a law firm offers a case in your coverage area.</p>}
      </div>
    );
  }

  return (
    <div>
      {demoEnabled && (
        <div className="mb-4 flex items-center justify-between rounded-2xl bg-cobalt/5 border border-cobalt/20 px-4 py-2.5" data-testid="th-demo-banner">
          <span className="text-xs font-semibold text-cobalt">DEMO MODE — these are fully interactive sample cases. Every button works exactly like production.</span>
          <button onClick={regenerate} disabled={busy} data-testid="th-regen-demo" className="flex items-center gap-1.5 text-xs font-semibold text-cobalt hover:underline">
            <RefreshCw className="w-3.5 h-3.5" /> Regenerate
          </button>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <div key={r.request_id} className="bg-white rounded-3xl border border-slate-100 p-5" data-testid={`th-request-${r.request_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-heading font-bold text-navy text-sm">{r.client_name || "Client (name unlocks on acceptance)"}</div>
                <span className="font-mono text-[11px] text-slate-400">{r.case_reference}</span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${PRIORITY_TONE[r.priority] || PRIORITY_TONE.Standard}`} data-testid={`th-priority-${r.request_id}`}>{r.priority}</span>
                {r.demo && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-cobalt/10 text-cobalt">Demo</span>}
              </div>
            </div>

            {r.requested_start_utc && (
              <div className="mt-3 rounded-2xl bg-navy text-white px-4 py-3 flex items-center justify-between" data-testid={`th-requested-time-${r.request_id}`}>
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-cobalt" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-white/60">Requested appointment</div>
                    <div className="text-sm font-bold">{r.requested_local_label || fmt(r.requested_start_utc)}</div>
                  </div>
                </div>
                <span className="text-[10px] text-white/60">{r.estimated_length_min} min visit</span>
              </div>
            )}
            {r.status === "time_proposed" && (
              <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-700" data-testid={`th-proposed-${r.request_id}`}>
                You proposed: {(r.proposed_times || []).map((t) => t.local_label).join(", ")} — awaiting client selection.
              </div>
            )}

            <div className="mt-3 text-xs text-slate-600 space-y-1">
              <div><b className="text-navy">{r.accident_category}</b> · {r.approximate_location}</div>
              <div>Symptoms: {r.reported_symptoms}</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5 text-[10px]">
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600"><Stethoscope className="w-3 h-3" /> RN {r.rn_name || (r.rn_review_completed ? "intake ✓" : "—")}</span>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500"><Scale className="w-3 h-3" /> {r.attorney_name || r.law_firm || "Attorney"}</span>
                {(r.insurance_verified || r.consent_status) && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600"><ShieldCheck className="w-3 h-3" /> {r.insurance_verified ? "Insurance verified" : "Consent ✓"}</span>}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => setDrawer(r)} data-testid={`th-review-${r.request_id}`}
                className="px-3.5 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" /> Review Case File</button>
              <button onClick={() => accept(r)} disabled={busy} data-testid={`th-accept-${r.request_id}`}
                className="px-3.5 py-2 rounded-full bg-navy text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Accept Appointment</button>
              <button onClick={() => setModal({ type: "propose", row: r, mode: "different_time" })} data-testid={`th-different-time-${r.request_id}`}
                className="px-3.5 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Request Different Time</button>
              <button onClick={() => setModal({ type: "propose", row: r, mode: "decline" })} data-testid={`th-decline-${r.request_id}`}
                className="px-3.5 py-2 rounded-full bg-white border border-red-200 text-red-500 text-xs font-semibold flex items-center gap-1"><X className="w-3.5 h-3.5" /> Decline</button>
              <button onClick={() => setModal({ type: "message", row: r })} data-testid={`th-message-${r.request_id}`}
                className="px-3.5 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Message</button>
            </div>
          </div>
        ))}
      </div>

      {drawer && <CaseFileDrawer row={drawer} onClose={() => setDrawer(null)} onAccept={() => accept(drawer)} busy={busy} />}
      {modal?.type === "propose" && <ProposeModal modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal?.type === "message" && <MessageModal modal={modal} onClose={() => setModal(null)} />}
      {modal?.type === "conflict" && (
        <Modal title="Scheduling conflict detected" onClose={() => setModal(null)} testid="th-conflict-modal">
          <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-600 space-y-1">
            {modal.conflicts.map((c, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {c}</div>)}
          </div>
          <div className="mt-3 text-xs font-semibold text-slate-500 uppercase">Available alternatives</div>
          <div className="mt-2 grid grid-cols-2 gap-2 max-h-52 overflow-y-auto">
            {modal.alternatives.map((s) => (
              <button key={s.start_utc} onClick={() => accept(modal.row, s.start_utc)} disabled={busy} data-testid="th-alt-slot"
                className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-600 hover:border-cobalt hover:text-cobalt text-left">{s.local_label}</button>
            ))}
            {!modal.alternatives.length && <div className="text-xs text-slate-400 col-span-2">No open alternatives in the next 10 days — adjust availability first.</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-navy text-sm">{title}</h3>
        <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100" data-testid={`${testid}-close`}><X className="w-4 h-4 text-slate-500" /></button>
      </div>
      {children}
    </div>
  </div>
);

const ProposeModal = ({ modal, onClose, onDone }) => {
  const decline = modal.mode === "decline";
  const [times, setTimes] = useState(["", "", ""]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/telehealth/requests/${modal.row.request_id}/propose-times`, {
        mode: modal.mode, note, times: times.filter(Boolean) });
      toast.success(decline ? "Appointment declined — the client was notified with your explanation." : "Alternative times sent to the client for selection.");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={decline ? "Decline appointment" : "Suggest different times"} onClose={onClose} testid="th-propose-modal">
      <p className="text-xs text-slate-400 mb-3">{decline ? "Explain the conflict. Optionally suggest new times — they are presented to the client immediately." : "Suggest up to 3 times. The client receives them instantly and picks one."}</p>
      {times.map((t, i) => (
        <input key={i} type="datetime-local" value={t} onChange={(e) => setTimes(times.map((x, j) => j === i ? e.target.value : x))}
          data-testid={`th-propose-time-${i}`} className={`${inputCls} mb-2`} />
      ))}
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="th-propose-note"
        className={inputCls} placeholder={decline ? "Explain the conflict (required)" : "Optional note to the client"} />
      <button onClick={submit} disabled={saving} data-testid="th-propose-submit"
        className={`mt-3 w-full py-2.5 rounded-full text-white text-sm font-semibold disabled:opacity-50 ${decline ? "bg-red-500" : "bg-navy"}`}>
        {saving ? "Sending…" : decline ? "Decline & notify client" : "Send suggested times"}
      </button>
    </Modal>
  );
};

const MessageModal = ({ modal, onClose }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await api.post(`/telehealth/requests/${modal.row.request_id}/message`, { text }); toast.success("Secure message sent to the client"); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Message ${modal.row.client_name || "client"}`} onClose={onClose} testid="th-message-modal">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="th-message-text" className={inputCls} placeholder="Type a secure message…" />
      <button onClick={send} disabled={saving} data-testid="th-message-send" className="mt-3 w-full py-2.5 rounded-full bg-navy text-white text-sm font-semibold disabled:opacity-50">{saving ? "Sending…" : "Send secure message"}</button>
    </Modal>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-5">
    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">{title}</div>
    {children}
  </div>
);

const CaseFileDrawer = ({ row, onClose, onAccept, busy }) => {
  const [file, setFile] = useState(undefined);
  useEffect(() => {
    api.get(`/telehealth/requests/${row.request_id}/file`).then(({ data }) => setFile(data)).catch(() => setFile(null));
  }, [row.request_id]);
  const cf = file?.case_file || {};
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-slate-50 w-full max-w-xl h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="th-case-file-drawer">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-navy">{cf.client?.name || row.client_name || "Case File"}</h3>
            <span className="font-mono text-xs text-slate-400">{file?.case_number || row.case_reference}</span>
          </div>
          <button onClick={onClose} data-testid="th-drawer-close" className="p-2 rounded-full hover:bg-slate-200"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        {file === undefined ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
        file === null ? <p className="text-sm text-slate-400">Could not load case file.</p> : (
          <>
            {cf.locked_note && <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">{cf.locked_note}</div>}
            {cf.requested_appointment && (
              <div className="mb-5 rounded-2xl bg-navy text-white p-4">
                <div className="text-[10px] uppercase text-white/60">Requested appointment</div>
                <div className="font-bold">{cf.requested_appointment.local_label || cf.requested_appointment.date} · {cf.requested_appointment.length_min || 30} min</div>
              </div>
            )}
            {cf.client && <Section title="Client Information">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs text-slate-600 space-y-1">
                <div className="font-semibold text-navy text-sm">{cf.client.name}</div>
                {cf.client.dob && <div>DOB: {cf.client.dob}</div>}
                <div>{cf.client.phone} · {cf.client.email}</div>
                {cf.client.address && <div>{cf.client.address}</div>}
              </div>
            </Section>}
            <Section title="Accident">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs text-slate-600 space-y-1">
                <div className="font-semibold text-navy">{cf.accident?.type}</div>
                <div>{cf.accident?.date} · {cf.accident?.location}</div>
                {cf.accident?.description && <div>{cf.accident.description}</div>}
                {cf.accident?.police_report && <div className="text-emerald-600 font-medium">Police report {cf.accident.police_report.number} — {cf.accident.police_report.agency}</div>}
              </div>
            </Section>
            {(cf.injuries || cf.injury_summary) && <Section title="Injury Summary">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs space-y-2">
                {cf.medical_urgency && <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cf.medical_urgency === "Serious" ? "bg-red-50 text-red-600" : cf.medical_urgency === "Moderate" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>Medical urgency: {cf.medical_urgency}</span>}
                {(cf.injuries || []).map((inj, i) => (
                  <div key={i} className="flex justify-between text-slate-600"><span><b className="text-navy">{inj.area}</b> — {inj.note}</span><span className="text-slate-400">{inj.severity}</span></div>
                ))}
                {!cf.injuries && <div className="text-slate-600">{cf.injury_summary}</div>}
              </div>
            </Section>}
            {cf.rn_intake && <Section title="RN Intake">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs text-slate-600 space-y-1" data-testid="th-file-rn">
                <div className="font-semibold text-emerald-600">{cf.rn_intake.completed ? "✓ Completed" : "Pending"} {cf.rn_intake.nurse ? `— ${cf.rn_intake.nurse}` : ""}</div>
                {cf.rn_intake.disposition && <div><b>Disposition:</b> {cf.rn_intake.disposition}</div>}
                {cf.rn_intake.notes && <div>{cf.rn_intake.notes}</div>}
                {cf.rn_intake.vitals && <div className="text-slate-400">BP {cf.rn_intake.vitals.bp} · HR {cf.rn_intake.vitals.hr} · Pain {cf.rn_intake.vitals.pain_scale}/10</div>}
              </div>
            </Section>}
            {cf.attorney && <Section title="Attorney">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs text-slate-600 space-y-1" data-testid="th-file-attorney">
                <div className="font-semibold text-navy">{cf.attorney.name || "—"} {cf.attorney.firm ? `· ${cf.attorney.firm}` : ""}</div>
                {cf.attorney.notes && <div className="italic">"{cf.attorney.notes}"</div>}
              </div>
            </Section>}
            {cf.insurance && <Section title="Insurance">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs text-slate-600 space-y-1">
                <div><b className="text-navy">{cf.insurance.carrier}</b> · {cf.insurance.policy} <span className="text-emerald-600 font-semibold ml-1">{cf.insurance.status}</span></div>
                {cf.insurance.other_carrier && <div className="text-slate-400">Other driver: {cf.insurance.other_carrier} · {cf.insurance.other_policy}</div>}
              </div>
            </Section>}
            {(cf.evidence || []).length > 0 && <Section title="Evidence & Uploads">
              <div className="grid grid-cols-2 gap-2">
                {cf.evidence.filter((e) => e.url).map((e, i) => (
                  <img key={i} src={e.url} alt={e.name} className="rounded-xl border border-slate-100 w-full h-24 object-cover" />
                ))}
              </div>
              <div className="mt-2 bg-white rounded-2xl border border-slate-100 p-3 text-xs text-slate-600 space-y-1">
                {cf.evidence.map((e, i) => <div key={i}>📎 {e.name}</div>)}
              </div>
            </Section>}
            {(cf.medical_history || []).length > 0 && <Section title="Medical History">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs text-slate-600 space-y-1">
                {cf.medical_history.map((h, i) => <div key={i}>• {h}</div>)}
              </div>
            </Section>}
            {(cf.documents || []).length > 0 && <Section title="Documents">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs space-y-1">
                {cf.documents.map((d, i) => <div key={i} className={d.uploaded ? "text-emerald-600" : "text-slate-400"}>{d.uploaded ? "✓" : "○"} {d.name}</div>)}
              </div>
            </Section>}
            {(cf.required_documentation || []).length > 0 && <Section title="Required Documentation (for attorney)">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs text-slate-600 space-y-1">
                {cf.required_documentation.map((d, i) => <div key={i}>• {d}</div>)}
              </div>
            </Section>}
            {(cf.timeline || []).length > 0 && <Section title="Case Timeline">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs space-y-2">
                {cf.timeline.map((t, i) => (
                  <div key={i} className="flex gap-2 text-slate-600"><span className="text-slate-300 shrink-0">{t.time ? new Date(t.time).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}</span> {t.title}</div>
                ))}
              </div>
            </Section>}
            {(cf.communications || []).length > 0 && <Section title="Previous Communications">
              <div className="bg-white rounded-2xl border border-slate-100 p-4 text-xs space-y-2">
                {cf.communications.map((m, i) => (
                  <div key={i}><span className="font-semibold text-navy">{m.from}</span> <span className="text-[10px] text-slate-300 uppercase">{m.role}</span><div className="text-slate-600">{m.text}</div></div>
                ))}
              </div>
            </Section>}
            {["offered", "time_proposed"].includes(file.status) && (
              <button onClick={onAccept} disabled={busy} data-testid="th-drawer-accept"
                className="w-full py-3 rounded-full bg-navy text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> Accept Appointment
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
